"use client";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PostComposer from "../../../../feed/components/HomePage/PostComposer/PostComposer";
import PostCard from "../../../../feed/components/HomePage/PostCard/PostCard";

import usePostsDoctor from "../../../../feed/hooks/usePostsDoctor";
import useSavedItems from "../../../../authorization/useSavedItems";
import "./DoctorPostsPanel.css";

/* timeAgo */
const formatTimeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  const map = [
    ["y", 31536000],
    ["mo", 2592000],
    ["w", 604800],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return "now";
};

export default function DoctorPostsPanel({ doctorId, doctorDisplay }) {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [isPublishing, setIsPublishing] = useState(false);
  const [updatingPostId, setUpdatingPostId] = useState(null);

  const safeDoctor = useMemo(
    () => ({
      fullName: doctorDisplay?.fullName || "Doctor",
      avatarUrl: doctorDisplay?.avatarUrl || "",
    }),
    [doctorDisplay]
  );

  // ✅ FIX: Utiliser mode="user" pour récupérer uniquement les posts du docteur
  const {
    posts,
    status,
    error,
    reload,
    uploadFiles,
    createPost,
    updatePost,
    deletePost,
    isUploading,
  } = usePostsDoctor(doctorId, { 
    autoFetch: !!doctorId, 
    pageSize: 10,
    mode: "user" // ✅ IMPORTANT: Mode "user" pour filtrer par doctorId
  });
  const { isSaved, toggleSave, pendingKey } = useSavedItems({ enabled: !!doctorId });

  const handlePublishPost = async (payload) => {
    if (!doctorId) return;
    setIsPublishing(true);
    try {
      const res = await createPost({ userId: doctorId, payload });
      if (res?.type?.includes("fulfilled")) {
        setCurrentPage(1);
        reload(1, 10);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleEditPost = async (postId, data) => {
    if (!postId) return;
    setUpdatingPostId(postId);
    try {
      const res = await updatePost(postId, doctorId, data);
      if (res?.type?.includes("fulfilled")) reload(currentPage, 10);
    } finally {
      setUpdatingPostId(null);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!postId) return;
    if (!window.confirm(t("doctorFeed.deleteConfirm"))) return;
    const res = await deletePost(postId, doctorId);
    if (res?.type?.includes("fulfilled")) {
      setCurrentPage(1);
      reload(1, 10);
    }
  };

  return (
    <div className="dposts-card">
      {/* ✅ FIXED HEADER (composer + alerts) */}
      <div className="dposts-head">
        <PostComposer
          onPublish={handlePublishPost}
          isPublishing={isPublishing}
          uploadFiles={uploadFiles}
          isUploading={isUploading}
        />

        {error && (
          <div className="dposts-error">
            ❌ {t("common.error")}: {String(error)}
          </div>
        )}

        {status === "loading" && (posts?.length ?? 0) === 0 && (
          <div className="dposts-loading">Loading posts...</div>
        )}
      </div>

      {/* ✅ SCROLL AREA */}
      <div className="dposts-body">
        <div className="dposts-list">
          {posts?.length ? (
            posts.map((p) => {
              // const pid = p.id || p._id;
              const pid = p.postId || p.id || p._id;
              const ownerId =
                p?.author?.id ||
                p?.author?._id ||
                p?.authorId ||
                p?.doctorId ||
                p?.userId ||
                doctorId;
              
              const authorName = p.author?.fullName || p.author?.name || safeDoctor.fullName;
              const authorAvatar = p.author?.avatar || p.author?.avatarUrl || safeDoctor.avatarUrl;
              
              return (
                <div key={pid} className="dposts-item">
                  <PostCard
                    postId={pid}
                    doctorName={authorName}
                    timeAgo={formatTimeAgo(p.createdAt)}
                    content={p.content || p.text}
                    image={p.imageUrl}
                    attachments={p.attachments || []}
                    likes={p.likesCount ?? p.likes ?? 0}
                    comments={p.commentsCount ?? p.comments ?? 0}
                    profileImage={authorAvatar}
                    onEdit={handleEditPost}
                    onDelete={handleDeletePost}
                    canManagePost={String(p?.userId ?? ownerId ?? "") === String(doctorId ?? "")}
                    isUpdating={updatingPostId === pid}
                    currentUserId={doctorId}
                    postOwnerId={ownerId}
                    isSaved={isSaved("post", pid)}
                    onToggleSave={(postId) => toggleSave("post", postId)}
                    isSaving={pendingKey === `post:${String(pid)}`}
                    createdAt={p.createdAt}
                  />
                </div>
              );
            })
          ) : (
            status !== "loading" && (
              <div className="dposts-empty">
                <div className="dposts-empty-icon">📭</div>
               <h3>{t("doctorFeed.empty.title")}</h3>
  <p>{t("doctorFeed.empty.subtitle")}</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}


